import { apiFetch, parseApiError } from 'app/javascript/services/api';
import { ClassSchedule } from 'app/javascript/types';

export interface PublicCompanySummary {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  kind: string;
  logo_url: string | null;
  member_count: number;
}

export interface PublicCompanyDetail {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  kind: string;
  address: string | null;
  city: string | null;
  state: string | null;
  website_url: string | null;
  phone: string | null;
  logo_url: string | null;
  has_store: boolean;
  members: PublicCompanyMember[];
  class_schedules: ClassSchedule[];
}

export interface PublicCompanyMember {
  id: number;
  name: string;
  nickname: string | null;
  slug: string;
  photo_url: string | null;
  role: string;
  current_belt: { rank: string } | null;
}

export interface MyCompanySummary {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  role: string;
  status: string;
  membership_id: number;
  can_manage: boolean;
  has_store: boolean;
}

export interface MyCompaniesResponse {
  owned: MyCompanySummary[];
  member: MyCompanySummary[];
  pending: MyCompanySummary[];
  rejected: MyCompanySummary[];
}

export interface CompanyWithMembership {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  kind: string;
  address: string | null;
  city: string | null;
  state: string | null;
  website_url: string | null;
  phone: string | null;
  logo_url: string | null;
  auto_approve_members: boolean;
  member_count: number;
  my_membership: {
    id: number;
    role: string;
    status: string;
    requested_at: string;
    accepted_at: string | null;
  } | null;
}

export interface MembershipResponse {
  id: number;
  user: {
    id: number;
    name: string;
    nickname: string | null;
    slug: string;
    photo_url?: string | null;
    current_belt?: { rank: string } | null;
    face_enrolled?: boolean;
    claimed?: boolean;
  };
  role: string;
  status: string;
  requested_at: string;
  accepted_at: string | null;
  month_check_in_count?: number;
  // Owner/admin view only: the member's guardian link (CPF of the responsible
  // adult). linked is true when a platform user has that CPF.
  guardian?: MembershipGuardian | null;
}

export interface MembershipGuardian {
  cpf: string;
  linked: boolean;
  name: string | null;
}

export interface MembershipsGrouped {
  accepted: MembershipResponse[];
  pending: MembershipResponse[];
}

export interface BulkAttendanceResponse {
  from: string;
  to: string;
  weekly_class_days: number;
  members: Record<string, Record<string, number>>;
}

export interface CreateCompanyParams {
  name: string;
  description?: string;
  kind?: string;
  address?: string;
  city?: string;
  state?: string;
  website_url?: string;
  phone?: string;
  auto_approve_members?: boolean;
}

const PUBLIC_PATH = '/api/public/companies';
const V1_PATH = '/api/v1/companies';

export const companiesService = {
  // Public endpoints
  async listPublic(params?: { search?: string; page?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', String(params.page));

    const response = await fetch(`${PUBLIC_PATH}?${searchParams}`);
    if (!response.ok) throw new Error('Failed to fetch companies');
    return response.json() as Promise<{
      companies: PublicCompanySummary[];
      pagy: { page: number; pages: number; count: number; next: number | null; prev: number | null };
    }>;
  },

  async getPublic(slug: string): Promise<PublicCompanyDetail> {
    const response = await fetch(`${PUBLIC_PATH}/${slug}`);
    if (!response.ok) throw new Error('Failed to fetch company');
    return response.json();
  },

  // Authenticated endpoints
  async myCompanies(): Promise<MyCompaniesResponse> {
    const response = await apiFetch(`${V1_PATH}/my_companies`);
    if (!response.ok) throw new Error('Failed to fetch my companies');
    return response.json();
  },

  async get(slug: string): Promise<CompanyWithMembership> {
    const response = await apiFetch(`${V1_PATH}/${slug}`);
    if (!response.ok) throw new Error('Failed to fetch company');
    return response.json();
  },

  async create(params: CreateCompanyParams): Promise<CompanyWithMembership> {
    const response = await apiFetch(V1_PATH, {
      method: 'POST',
      body: JSON.stringify({ company: params }),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao criar equipe'));
    }
    return response.json();
  },

  async update(slug: string, params: Partial<CreateCompanyParams>): Promise<CompanyWithMembership> {
    const response = await apiFetch(`${V1_PATH}/${slug}`, {
      method: 'PATCH',
      body: JSON.stringify({ company: params }),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao atualizar equipe'));
    }
    return response.json();
  },

  async delete(slug: string): Promise<void> {
    const response = await apiFetch(`${V1_PATH}/${slug}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete company');
  },

  async uploadLogo(slug: string, file: File): Promise<{ logo_url: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiFetch(`${V1_PATH}/${slug}/upload_logo`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) throw new Error('Failed to upload logo');
    return response.json();
  },

  async deleteLogo(slug: string): Promise<void> {
    const response = await apiFetch(`${V1_PATH}/${slug}/delete_logo`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete logo');
  },

  // Membership endpoints
  async getMemberships(companySlug: string): Promise<MembershipsGrouped> {
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships`);
    if (!response.ok) throw new Error('Failed to fetch memberships');
    return response.json();
  },

  async requestJoin(companySlug: string): Promise<MembershipResponse> {
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao solicitar entrada'));
    }
    return response.json();
  },

  async addMember(companySlug: string, userId: number): Promise<MembershipResponse> {
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao adicionar membro'));
    }
    return response.json();
  },

  async preRegisterMember(
    companySlug: string,
    data: { name: string; email: string; belt_rank?: string; guardian_cpf?: string }
  ): Promise<MembershipResponse> {
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao pré-cadastrar membro'));
    }
    return response.json();
  },

  // Guardian endpoints (owner/admin only)
  async previewGuardian(
    companySlug: string,
    membershipId: number,
    cpf: string
  ): Promise<{ linked: boolean; name: string | null }> {
    const params = new URLSearchParams({ cpf });
    const response = await apiFetch(
      `${V1_PATH}/${companySlug}/memberships/${membershipId}/guardian?${params}`
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao buscar responsável'));
    }
    return response.json();
  },

  async updateGuardian(
    companySlug: string,
    membershipId: number,
    guardianCpf: string
  ): Promise<MembershipResponse> {
    const response = await apiFetch(
      `${V1_PATH}/${companySlug}/memberships/${membershipId}/guardian`,
      {
        method: 'PATCH',
        body: JSON.stringify({ guardian_cpf: guardianCpf }),
      }
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao salvar responsável'));
    }
    return response.json();
  },

  async checkEmail(
    companySlug: string,
    email: string
  ): Promise<{
    status: 'already_member' | 'user_found' | 'not_found';
    user?: { id: number; name: string; nickname: string | null; email: string; photo_url: string | null; current_belt: { rank: string } | null };
    membership?: MembershipResponse;
  }> {
    const searchParams = new URLSearchParams({ email });
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships/check_email?${searchParams}`);
    if (!response.ok) throw new Error('Erro ao verificar e-mail');
    return response.json();
  },

  async acceptMembership(companySlug: string, membershipId: number): Promise<MembershipResponse> {
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships/${membershipId}/accept`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error(await parseApiError(response, 'Erro ao aceitar membro'));
    return response.json();
  },

  async rejectMembership(companySlug: string, membershipId: number): Promise<MembershipResponse> {
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships/${membershipId}/reject`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to reject membership');
    return response.json();
  },

  async removeMembership(companySlug: string, membershipId: number): Promise<void> {
    const response = await apiFetch(`${V1_PATH}/${companySlug}/memberships/${membershipId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to remove membership');
  },

  async getMembershipsAttendance(
    companySlug: string,
    options: { from?: string; to?: string } = {}
  ): Promise<BulkAttendanceResponse> {
    const qs = new URLSearchParams();
    if (options.from) qs.set('from', options.from);
    if (options.to) qs.set('to', options.to);
    const query = qs.toString();
    const url = `${V1_PATH}/${companySlug}/memberships/attendance${query ? `?${query}` : ''}`;
    const response = await apiFetch(url);
    if (!response.ok) throw new Error('Failed to fetch attendance');
    return response.json();
  },
};
